import * as React from "react";
import wishlist from "./wishlist.module.scss";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import Item from "@src/components/modals/ItemModal";
import { useQuery } from "@apollo/client";
import {
  Currency,
  EventSupportCurrencyProvider,
  EventWishlistNoteQuery,
  EventWishlistNoteQueryVariables,
  GetForeignAccountDetailsQuery,
  GetWishListItemsQuery,
  GetWishListItemsQueryVariables,
} from "@src/@types/api.d";
import { EVENT_WISHLIST_NOTE, GetWishListItems } from "@src/graphql/queries";
import Icon from "@src/components/Icon";
import getSymbolFromCurrency from "currency-symbol-map";
import DialogModal from "@src/components/Dialog";
import ForeignDetails from "../CashGiftModal/modals/ForeignDetails";
import { LoaderIcon } from "react-hot-toast";
import WishlistItem from "@src/components/modals/WishlistModal/WishlistItem";
import { AvailablePaymentCurrencies } from "@src/components/BirthdayTemplate1/AsoEbiLink";
import { WishlistItemType } from "@src/components/BirthdayTemplate2/Wishlist";

interface WishlistProps {
  eventId: number;
  eventUuid: string | undefined;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  foreignDetails:
    | GetForeignAccountDetailsQuery["getForeignAccountDetails"]
    | null
    | undefined;
  availableGiftPaymentCurrencies?: AvailablePaymentCurrencies;
}

const Wishlist = ({
  eventId,
  eventUuid,
  setOpen,
  foreignDetails,
  availableGiftPaymentCurrencies,
}: WishlistProps) => {
  const renderNoteWithLinks = (text: string) => {
    const parts = text.split(/(https?:\/\/[^\s]+)/g);
    return parts.map((part, i) =>
      /^https?:\/\//.test(part) ? (
        <a key={i} href={part} target="_blank" rel="noreferrer noopener">
          {part}
        </a>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );
  };

  const [openItem, setOpenItem] = React.useState<boolean>(false);

  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize] = React.useState(10); // Reduced from 50 to 10 for better UX

  const [provider, setProvider] =
    React.useState<EventSupportCurrencyProvider | null>(null);

  // Reset to page 1 when eventId changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [eventId]);

  const { data: getWishListItemsData, loading: getWishListItemsLoading } =
    useQuery<GetWishListItemsQuery, GetWishListItemsQueryVariables>(
      GetWishListItems,
      {
        skip: !eventUuid,
        variables: {
          eventId: `${eventUuid}`,
          page: currentPage,
          pageSize: pageSize,
        },
      }
    );

  const { data: getNotesByEventData } = useQuery<
    EventWishlistNoteQuery,
    EventWishlistNoteQueryVariables
  >(EVENT_WISHLIST_NOTE, {
    skip: !eventId,
    variables: {
      eventId,
    },
  });

  const [openItemModal, setOpenItemModal] = React.useState(false);
  const [openForeignDetails, setOpenForeignDetails] = React.useState(false);
  const [selectedItem, setSelectedItem] =
    React.useState<WishlistItemType | null>(null);
  const [selectedForeignItem, setSelectedForeignItem] =
    React.useState<any>(null);

  const [foreignDetail, setForeignDetail] =
    React.useState<
      GetForeignAccountDetailsQuery["getForeignAccountDetails"][number]
    >(null);
  const [showNotes, setShowNotes] = React.useState(false);

  const handleSendMoney = (item: WishlistItemType) => {
    // Find all entries for this currency in availableGiftPaymentCurrencies
    const currencyConfigs =
      availableGiftPaymentCurrencies?.filter(
        (entry) => entry?.currency === item?.currency
      ) || [];

    // Check if any entry has PAYSTACK provider
    const hasPaystack = currencyConfigs.some(
      (config) => config?.provider === EventSupportCurrencyProvider.Paystack
    );

    // Check if any entry has FLUTTERWAVE provider
    const hasFlutterwave = currencyConfigs.some(
      (config) => config?.provider === EventSupportCurrencyProvider.Flutterwave
    );

    if (hasPaystack) {
      setProvider(EventSupportCurrencyProvider.Paystack);
      setSelectedItem(item);
      setOpenItemModal(true);
    } else if (hasFlutterwave) {
      setProvider(EventSupportCurrencyProvider.Flutterwave);
      setSelectedItem(item);
      setOpenItemModal(true);
    } else {
      setForeignDetail(
        foreignDetails?.find((detail) => detail?.currency === item?.currency) ??
          null
      );
      setSelectedForeignItem(item);
      setOpenForeignDetails(true);
    }
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    setCurrentPage(currentPage + 1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Filter items by enabled currencies
  const filteredItems = React.useMemo(() => {
    return (
      getWishListItemsData?.getWishListItems?.filter((item) =>
        availableGiftPaymentCurrencies
          ?.map((currency) => currency?.currency)
          .includes(item?.currency as Currency)
      ) || []
    );
  }, [getWishListItemsData?.getWishListItems, availableGiftPaymentCurrencies]);

  // Calculate pagination info
  const totalItems = filteredItems.length;
  const hasNextPage =
    (getWishListItemsData?.getWishListItems?.length || 0) === pageSize; // If we got a full page, there might be more
  const hasPreviousPage = currentPage > 1;
  const isEmpty = !getWishListItemsLoading && totalItems === 0;

  const wishlistNote = getNotesByEventData?.eventWishlistNote ?? "";
  const hasWishlistNotes = !!wishlistNote;

  return (
    <div className={wishlist["wishlist--content"]}>
      <div className={wishlist["wishlist--content__top"]}>
        <h1>Wedding Wishlist</h1>
        {hasWishlistNotes && (
          <button
            type="button"
            className={wishlist["wishlist--content__top__notes-toggle"]}
            onClick={() => setShowNotes((prev) => !prev)}
          >
            {showNotes ? "Hide notes" : "Show notes"}
          </button>
        )}
      </div>

      {showNotes && hasWishlistNotes && (
        <div className={wishlist["wishlist--notes"]}>
          <p>{renderNoteWithLinks(wishlistNote)}</p>
        </div>
      )}

      <ScrollArea.Root className={wishlist["wishlist--content__scroll"]}>
        <ScrollArea.Viewport className={wishlist["wishlist--content__bottom"]}>
          {getWishListItemsLoading ? (
            <div className="flex justify-center items-center flex-grow">
              <LoaderIcon />
            </div>
          ) : filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <WishlistItem
                item={item}
                handleSendMoney={handleSendMoney}
                key={item?.id}
              />
            ))
          ) : (
            <div className={wishlist["wishlist--empty"]}>
              {currentPage === 1
                ? "No wishlist items available"
                : "No more items on this page"}
            </div>
          )}
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar
          className={wishlist["wishlist--content__scroll__bar"]}
          orientation="vertical"
        >
          <ScrollArea.Thumb
            className={wishlist["wishlist--content__scroll__thumb"]}
          />
        </ScrollArea.Scrollbar>
        <ScrollArea.Scrollbar
          className={wishlist["wishlist--content__scroll__bar"]}
          orientation="horizontal"
        >
          <ScrollArea.Thumb
            className={wishlist["wishlist--content__scroll__thumb"]}
          />
        </ScrollArea.Scrollbar>
        {/* <ScrollArea.Corner /> */}
      </ScrollArea.Root>

      {/* Pagination Controls - only show if there are items or loading */}
      {!isEmpty && (
        <div className={wishlist["wishlist--pagination"]}>
          <div className={wishlist["wishlist--pagination__info"]}>
            <span>Page {currentPage}</span>
            {totalItems > 0 && (
              <span>
                Showing {totalItems} item{totalItems !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          <div className={wishlist["wishlist--pagination__controls"]}>
            <button
              className={wishlist["wishlist--pagination__btn"]}
              onClick={handlePreviousPage}
              disabled={!hasPreviousPage || getWishListItemsLoading}
            >
              Previous
            </button>

            <button
              className={wishlist["wishlist--pagination__btn"]}
              onClick={handleNextPage}
              disabled={!hasNextPage || getWishListItemsLoading}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Item Modal - moved outside the map loop */}
      <DialogModal open={openItemModal} onOpenChange={setOpenItemModal} asChild>
        {selectedItem && (
          <Item
            title={selectedItem?.name ?? ""}
            id={selectedItem?.id ?? ""}
            amount={!!selectedItem?.amount ? selectedItem?.amount : 0}
            eventId={eventId}
            eventUuid={eventUuid}
            setOpen={setOpen}
            setOpenItem={setOpenItem}
            currencyToPay={selectedItem?.currency as Currency}
            provider={provider}
          />
        )}
      </DialogModal>

      {/* Foreign Details Modal */}
      <DialogModal
        open={openForeignDetails}
        onOpenChange={setOpenForeignDetails}
        asChild
      >
        <ForeignDetails
          foreignDetail={foreignDetail}
          availableGiftPaymentCurrencies={availableGiftPaymentCurrencies}
          eventId={eventId}
          eventUuid={`${eventUuid}`}
          setOpen={setOpen}
          initialAmount={selectedForeignItem?.amount}
          itemName={selectedForeignItem?.name}
          foreignCurrency={selectedForeignItem?.currency as Currency}
        />
      </DialogModal>
    </div>
  );
};

export const MemoWishlist = React.memo(Wishlist);
